/**
 * SizingStateService — single source of truth for sizing measurement state.
 *
 * Manages keypoint placements, diameter edits, custom offsets/measurements,
 * and computed measurement results. Broadcasts PubSub events on every change.
 */

import { PubSubService } from '../../core/pubsub-service';
import type { IServiceManager, ServiceContext, ServiceRegistration } from '../../core/types';
import type { Centerline, CenterlineNode } from '../../domain/centerline';
import type { Keypoint } from '../../domain/keypoint';
import {
    MeasurementEngine,
    buildDependencyGraph,
    getAffectedMeasurements,
    getAffectedByDiameterEdit,
    computeLength,
    computeAngleAtNodes,
} from '../../domain/sizing/computation';
import type { ComputationContext, DependencyGraph } from '../../domain/sizing/computation';
import { DiameterEdit } from '../../domain/sizing/diameter-edit';
import { KeypointPlacement } from '../../domain/sizing/keypoint-placement';
import { MeasurementResult } from '../../domain/sizing/measurement-result';
import type {
    ISizingTemplate,
    IOffsetDiameterMeasurementDef,
    IKeypointDefinition,
} from '../../domain/sizing/templates/types';
import { MeasurementType, MeasurementUnit, MeasurementCategory } from '../../domain/sizing/templates/types';
import type { ISizingStateDTO, ICustomMeasurementDTO } from '../../domain/sizing/types';
import type { Vec3 } from '../../domain/types';
import { getNavigationNodeId } from '../../utils/measurement-helpers';
import { CurrentLocationService } from '../current-location-service';

// ============================================================================
// Event Payloads
// ============================================================================

export interface KeypointMovedPayload {
    readonly keypointId: string;
    readonly newNodeId: number;
    readonly previousNodeId: number;
}

export interface DiameterEditedPayload {
    readonly nodeId: number;
    readonly editedValue: number;
    readonly originalValue: number;
}

export interface MeasurementRelocatedPayload {
    readonly measurementId: string;
    readonly newNodeId: number;
}

export interface MeasurementsUpdatedPayload {
    readonly updatedIds: readonly string[];
}

// ============================================================================
// Event Map
// ============================================================================

/** Maps each sizing event name to its payload type. */
export interface SizingEventMap {
    'sizing:initialized': undefined;
    'sizing:keypoint:moved': KeypointMovedPayload;
    'sizing:keypoint:reset': { keypointId: string };
    'sizing:diameter:edited': DiameterEditedPayload;
    'sizing:diameter:restored': { nodeId: number };
    'sizing:measurements:updated': MeasurementsUpdatedPayload;
    'sizing:custom-measurement:added': { id: string };
    'sizing:custom-measurement:removed': { id: string };
    'sizing:custom-measurement:renamed': { id: string; newName: string };
    'sizing:measurement:relocated': MeasurementRelocatedPayload;
    'sizing:offset:added': { offsetId: string; keypointId: string; offsetMm: number };
    'sizing:offset:removed': { offsetId: string };
    'sizing:measurement-selection:changed': { selectedId: string | null };
    'sizing:custom-measurement:draft-changed': { draft: CustomMeasurementDraft | null };
}

// ============================================================================
// Domain Types
// ============================================================================

export interface DiameterGroup {
    keypointId: string;
    main: MeasurementResult;
    offsets: MeasurementResult[];
}

export interface DiameterGroupsResult {
    groups: DiameterGroup[];
    minMax: MeasurementResult[];
    custom: MeasurementResult[];
}

// ============================================================================
// Custom-measurement draft (in-progress creation)
// ============================================================================

export type DraftKind = 'length' | 'angle';
export type LengthSlot = 'start' | 'end';
export type AngleSlot = 'p1' | 'vertex' | 'p2';
export type DraftSlot = LengthSlot | AngleSlot;

export interface CustomMeasurementDraft {
    readonly kind: DraftKind;
    /** slot → nodeId (absent = not yet placed). Slot order defines marker order. */
    readonly points: Partial<Record<DraftSlot, number>>;
}

const LENGTH_SLOTS: readonly LengthSlot[] = ['start', 'end'];
const ANGLE_SLOTS: readonly AngleSlot[] = ['p1', 'vertex', 'p2'];

/**
 * Custom angles report the deviation-from-straight (supplementary) angle, matching
 * both the EndoSize convention used by template angles and the AngleOverlay wedge,
 * which renders the supplementary arc (isLongestArcMode).
 */
const CUSTOM_ANGLE_FORMULA = 'supplementary' as const;

function requiredSlots(kind: DraftKind): readonly DraftSlot[] {
    return kind === 'length' ? LENGTH_SLOTS : ANGLE_SLOTS;
}

// ============================================================================
// Service
// ============================================================================

export class SizingStateService extends PubSubService {
    static readonly SERVICE_NAME = 'SizingStateService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: SizingStateService.SERVICE_NAME,
        factory: (context: ServiceContext) => new SizingStateService(context),
    };

    readonly EVENTS = {
        SIZING_INITIALIZED: 'sizing:initialized',
        KEYPOINT_MOVED: 'sizing:keypoint:moved',
        KEYPOINT_RESET: 'sizing:keypoint:reset',
        DIAMETER_EDITED: 'sizing:diameter:edited',
        DIAMETER_RESTORED: 'sizing:diameter:restored',
        MEASUREMENTS_UPDATED: 'sizing:measurements:updated',
        CUSTOM_MEASUREMENT_ADDED: 'sizing:custom-measurement:added',
        CUSTOM_MEASUREMENT_REMOVED: 'sizing:custom-measurement:removed',
        CUSTOM_MEASUREMENT_RENAMED: 'sizing:custom-measurement:renamed',
        MEASUREMENT_RELOCATED: 'sizing:measurement:relocated',
        OFFSET_ADDED: 'sizing:offset:added',
        OFFSET_REMOVED: 'sizing:offset:removed',
        MEASUREMENT_SELECTION_CHANGED: 'sizing:measurement-selection:changed',
        CUSTOM_MEASUREMENT_DRAFT_CHANGED: 'sizing:custom-measurement:draft-changed',
    } as const;

    // ── Template & reference data ──
    private template: ISizingTemplate | null = null;
    private centerlines: Map<string, Centerline> = new Map();
    private nodePool: Map<number, CenterlineNode> = new Map();

    // ── Initial state (for reset) ──
    private initialPlacements: Map<string, KeypointPlacement> = new Map();

    // ── Mutable state ──
    private placements: Map<string, KeypointPlacement> = new Map();
    private diameterEdits: Map<number, DiameterEdit> = new Map();
    private customOffsets: IOffsetDiameterMeasurementDef[] = [];
    /** Tracks which measurement IDs are user-added custom measurements (for serialization) */
    private customMeasurementIds: Set<string> = new Set();
    private measurements: Map<string, MeasurementResult> = new Map();
    private ghostPlacements: Map<string, KeypointPlacement[]> = new Map();
    private visibility: Map<string, boolean> = new Map();

    // ── Selection state ──
    private selectedMeasurementId: string | null = null;

    // ── Dependency graph (for incremental recomputation) ──
    private depGraph: DependencyGraph | null = null;

    private nextOffsetId = 1;
    private nextCustomLengthId = 1;
    private nextCustomDiameterId = 1;
    private nextCustomAngleId = 1;

    /** Transient in-progress custom measurement (never persisted). */
    private draft: CustomMeasurementDraft | null = null;

    private readonly serviceManager: IServiceManager;

    constructor(context: ServiceContext) {
        super();
        this.serviceManager = context.services;
    }

    // ========================================================================
    // Initialization
    // ========================================================================

    /**
     * Initialize sizing from template and vascular description data.
     * Projects keypoints onto centerlines and computes all initial measurements.
     */
    initialize(
        template: ISizingTemplate,
        centerlines: Map<string, Centerline>,
        keypoints: Map<string, Keypoint>
    ): void {
        this.template = template;
        this.centerlines = centerlines;
        this.buildNodePool();
        this.depGraph = buildDependencyGraph(template);

        // Project keypoints onto centerline nodes
        for (const kpDef of template.keypoints) {
            const kp = keypoints.get(kpDef.id);
            if (!kp) continue;

            const placement = this.projectKeypointOntoNode(kpDef, kp.position);
            if (placement) {
                this.placements.set(kpDef.id, placement);
                this.initialPlacements.set(kpDef.id, placement);
            }
        }

        // Compute ghost placements for keypoints that span multiple legs
        this.updateAllGhostPlacements();

        // Compute all measurements
        this.measurements = MeasurementEngine.computeAll(this.buildComputationContext());

        this.emit(this.EVENTS.SIZING_INITIALIZED, undefined);
    }

    /**
     * Load a previously saved sizing state, applying edits on top of initial state.
     */
    loadState(dto: ISizingStateDTO): void {
        if (!this.template) {
            throw new Error('Cannot load state before initialization');
        }

        if (dto.version !== 1) {
            console.warn(
                `[SizingStateService] Unsupported DTO version ${dto.version}, skipping state restoration.`
            );
            return;
        }

        if (dto.templateId !== this.template.id) {
            console.warn(
                `[SizingStateService] Template mismatch: DTO has "${dto.templateId}" but current template is "${this.template.id}". Skipping state restoration.`
            );
            return;
        }

        // Idempotency: clear previous custom state to prevent duplication on repeated calls.
        this.customOffsets = [];
        for (const id of this.customMeasurementIds) {
            this.measurements.delete(id);
        }
        this.customMeasurementIds.clear();
        this.nextOffsetId = 1;
        this.nextCustomLengthId = 1;
        this.nextCustomDiameterId = 1;
        this.nextCustomAngleId = 1;

        const templateKeypointIds = new Set(this.template.keypoints.map((kp) => kp.id));

        // Apply edited keypoints
        for (const edited of dto.editedKeypoints) {
            const node = this.nodePool.get(edited.nodeId);
            if (!node) {
                console.warn(
                    `[SizingStateService] Skipping edited keypoint "${edited.keypointId}": node ${edited.nodeId} not found in node pool.`
                );
                continue;
            }
            this.placements.set(
                edited.keypointId,
                new KeypointPlacement(edited.keypointId, edited.nodeId, node.point, true)
            );
        }

        // Apply diameter edits
        for (const edit of dto.diameterEdits) {
            const node = this.nodePool.get(edit.nodeId);
            if (!node) {
                console.warn(
                    `[SizingStateService] Skipping diameter edit: node ${edit.nodeId} not found in node pool.`
                );
                continue;
            }
            this.diameterEdits.set(
                edit.nodeId,
                new DiameterEdit(edit.nodeId, edit.editedValue, node.diameter.value, edit.p1, edit.p2)
            );
        }

        // Apply custom offsets
        for (const offset of dto.customOffsets) {
            if (!templateKeypointIds.has(offset.keypointId)) {
                console.warn(
                    `[SizingStateService] Skipping custom offset: keypoint "${offset.keypointId}" not found in template.`
                );
                continue;
            }
            this.addOffsetDiameterInternal(offset.keypointId, offset.offsetMm, false);
        }

        // Apply visibility
        for (const [id, visible] of Object.entries(dto.visibility)) {
            this.visibility.set(id, visible);
        }

        // Recompute ghost placements with loaded edits
        this.updateAllGhostPlacements();

        // Recompute all template measurements with loaded edits
        this.measurements = MeasurementEngine.computeAll(this.buildComputationContext());

        // Apply custom measurements after computeAll (which replaces the map)
        for (const cm of dto.customMeasurements) {
            if (cm.type === 'custom-length') {
                this.addCustomLength(cm.fromNodeId, cm.toNodeId, cm.name, false);
            } else if (cm.type === 'custom-diameter') {
                this.addCustomDiameter(cm.nodeId, cm.name, false);
            } else if (cm.type === 'custom-angle') {
                this.addCustomAngle(cm.nodeIds, cm.name, false);
            }
        }

        this.emit(this.EVENTS.SIZING_INITIALIZED, undefined);
    }

    // ========================================================================
    // Keypoint Management
    // ========================================================================

    /**
     * Move a keypoint to a new centerline node.
     * Recomputes affected measurements and broadcasts events.
     */
    moveKeypoint(keypointId: string, newNodeId: number): void {
        const current = this.placements.get(keypointId);
        if (!current) return;

        const kpDef = this.template?.keypoints.find((k) => k.id === keypointId);
        if (!kpDef?.editable) return;

        const node = this.nodePool.get(newNodeId);
        if (!node) return;

        const previousNodeId = current.nodeId;
        this.placements.set(keypointId, current.withNode(newNodeId, node.point));

        this.updateGhostPlacementsForKeypoint(keypointId);

        // Incremental recomputation
        const updatedIds = this.recomputeForKeypoint(keypointId);

        this.emit(this.EVENTS.KEYPOINT_MOVED, {
            keypointId,
            newNodeId,
            previousNodeId,
        });
        this.broadcastMeasurementsUpdated(updatedIds);
    }

    /**
     * Reset a keypoint to its initial position.
     */
    resetKeypoint(keypointId: string): void {
        const initial = this.initialPlacements.get(keypointId);
        if (!initial) return;

        this.placements.set(keypointId, initial);

        this.updateGhostPlacementsForKeypoint(keypointId);

        const updatedIds = this.recomputeForKeypoint(keypointId);

        this.emit(this.EVENTS.KEYPOINT_RESET, { keypointId });
        this.broadcastMeasurementsUpdated(updatedIds);
    }

    getPlacement(keypointId: string): KeypointPlacement | undefined {
        return this.placements.get(keypointId);
    }

    getAllPlacements(): ReadonlyMap<string, KeypointPlacement> {
        return this.placements;
    }

    // ========================================================================
    // Diameter Management
    // ========================================================================

    /**
     * Set an edited diameter at a node.
     */
    editDiameter(nodeId: number, editedValue: number, p1: Vec3, p2: Vec3): void {
        const node = this.nodePool.get(nodeId);
        if (!node) return;

        this.diameterEdits.set(nodeId, new DiameterEdit(nodeId, editedValue, node.diameter.value, p1, p2));

        const updatedIds = this.recomputeForDiameterEdit(nodeId);

        this.emit(this.EVENTS.DIAMETER_EDITED, {
            nodeId,
            editedValue,
            originalValue: node.diameter.value,
        });
        this.broadcastMeasurementsUpdated(updatedIds);
    }

    /**
     * Restore a diameter to its original computed value.
     */
    restoreDiameter(nodeId: number): void {
        if (!this.diameterEdits.has(nodeId)) return;

        this.diameterEdits.delete(nodeId);
        const updatedIds = this.recomputeForDiameterEdit(nodeId);

        this.emit(this.EVENTS.DIAMETER_RESTORED, { nodeId });
        this.broadcastMeasurementsUpdated(updatedIds);
    }

    getDiameterEdit(nodeId: number): DiameterEdit | undefined {
        return this.diameterEdits.get(nodeId);
    }

    getAllDiameterEdits(): ReadonlyMap<number, DiameterEdit> {
        return this.diameterEdits;
    }

    /**
     * Relocate a diameter measurement to a new node.
     * Updates the measurement's value, navigation target, and context to reflect
     * the diameter at the new node. Used when the user picks a specific slice
     * for a min/max or custom diameter measurement.
     */
    relocateMeasurement(measurementId: string, newNodeId: number): void {
        const existing = this.measurements.get(measurementId);
        if (!existing) return;

        const RELOCATABLE_CONTEXT_TYPES: ReadonlySet<string> = new Set([
            'min-max-diameter',
            'custom-diameter',
            'diameter',
            'offset-diameter',
        ]);

        if (!RELOCATABLE_CONTEXT_TYPES.has(existing.context.type)) return;

        const node = this.nodePool.get(newNodeId);
        if (!node) return;

        const updated = existing.with({
            value: node.diameter.value,
            navigationNodeId: newNodeId,
            hasEditedInputs: true,
            context: { ...existing.context, nodeId: newNodeId } as typeof existing.context,
        });

        this.measurements.set(measurementId, updated);

        this.emit(this.EVENTS.MEASUREMENT_RELOCATED, {
            measurementId,
            newNodeId,
        });
        this.broadcastMeasurementsUpdated([measurementId]);
    }

    // ========================================================================
    // Offset Diameter Management
    // ========================================================================

    /**
     * Add a custom offset diameter from a keypoint.
     * Not allowed from P1, P7, P8 (non-editable endpoints).
     */
    addOffsetDiameter(keypointId: string, offsetMm: number): void {
        this.addOffsetDiameterInternal(keypointId, offsetMm, true);
    }

    /**
     * Remove a custom offset diameter by ID. Default offsets (from template) cannot be removed.
     */
    removeOffsetDiameter(offsetId: string): void {
        const idx = this.customOffsets.findIndex((o) => o.id === offsetId);
        if (idx < 0) return;

        this.customOffsets.splice(idx, 1);
        this.measurements.delete(offsetId);

        this.emit(this.EVENTS.OFFSET_REMOVED, { offsetId });
        this.broadcastMeasurementsUpdated([offsetId]);
    }

    // ========================================================================
    // Custom Measurements
    // ========================================================================

    /**
     * Add a user-created centerline-following length measurement between two nodes.
     */
    addCustomLength(fromNodeId: number, toNodeId: number, name?: string, broadcast = true): void {
        const lengthResult = computeLength(fromNodeId, toNodeId, this.centerlines);
        if (!lengthResult) return;

        const counter = this.nextCustomLengthId++;
        const id = `custom-length-${counter}`;
        const displayName = name ?? `L Custom ${counter}`;
        const result = new MeasurementResult(
            id,
            MeasurementType.LENGTH,
            MeasurementCategory.LENGTH,
            displayName,
            displayName,
            lengthResult.value,
            MeasurementUnit.MM,
            fromNodeId,
            false,
            { type: 'custom-length', fromNodeId, toNodeId }
        );

        this.measurements.set(id, result);
        this.customMeasurementIds.add(id);

        if (broadcast) {
            this.emit(this.EVENTS.CUSTOM_MEASUREMENT_ADDED, { id });
            this.broadcastMeasurementsUpdated([id]);
        }
    }

    /**
     * Add a user-created diameter measurement at a node.
     * Derives p1/p2 from the node pool (or from a diameter edit if present).
     */
    addCustomDiameter(nodeId: number, name?: string, broadcast = true): void {
        const node = this.nodePool.get(nodeId);
        if (!node) return;

        const edit = this.diameterEdits.get(nodeId);
        const value = edit?.editedValue ?? node.diameter.value;

        const counter = this.nextCustomDiameterId++;
        const id = `custom-diameter-${counter}`;
        const displayName = name ?? `D Custom ${counter}`;
        const result = new MeasurementResult(
            id,
            MeasurementType.DIAMETER,
            MeasurementCategory.DIAMETER,
            displayName,
            displayName,
            value,
            MeasurementUnit.MM,
            nodeId,
            false,
            { type: 'custom-diameter', nodeId }
        );

        this.measurements.set(id, result);
        this.customMeasurementIds.add(id);

        if (broadcast) {
            this.emit(this.EVENTS.CUSTOM_MEASUREMENT_ADDED, { id });
            this.broadcastMeasurementsUpdated([id]);
        }
    }

    /**
     * Add a user-created angle measurement at the vertex (middle) of three nodes.
     */
    addCustomAngle(nodeIds: readonly [number, number, number], name?: string, broadcast = true): void {
        const value = computeAngleAtNodes(nodeIds, this.nodePool, CUSTOM_ANGLE_FORMULA);
        if (value === null) return;

        const counter = this.nextCustomAngleId++;
        const id = `custom-angle-${counter}`;
        const displayName = name ?? `A Custom ${counter}`;
        const result = new MeasurementResult(
            id,
            MeasurementType.ANGLE,
            MeasurementCategory.ANGLE,
            displayName,
            displayName,
            value,
            MeasurementUnit.DEGREES,
            nodeIds[1], // navigate to the vertex
            false,
            { type: 'custom-angle', nodeIds, formula: CUSTOM_ANGLE_FORMULA }
        );

        this.measurements.set(id, result);
        this.customMeasurementIds.add(id);

        if (broadcast) {
            this.emit(this.EVENTS.CUSTOM_MEASUREMENT_ADDED, { id });
            this.broadcastMeasurementsUpdated([id]);
        }
    }

    /**
     * Rename a custom measurement.
     */
    renameCustomMeasurement(id: string, newName: string): void {
        if (!this.customMeasurementIds.has(id)) return;
        const existing = this.measurements.get(id);
        if (!existing) return;

        const renamed = existing.with({ name: newName, shortLabel: newName });
        this.measurements.set(id, renamed);
        this.emit(this.EVENTS.CUSTOM_MEASUREMENT_RENAMED, { id, newName });
        this.broadcastMeasurementsUpdated([id]);
    }

    /**
     * Remove a custom measurement.
     */
    removeCustomMeasurement(id: string): void {
        if (!this.customMeasurementIds.has(id)) return;
        this.customMeasurementIds.delete(id);
        this.measurements.delete(id);
        this.emit(this.EVENTS.CUSTOM_MEASUREMENT_REMOVED, { id });
    }

    getCustomMeasurementIds(): ReadonlySet<string> {
        return this.customMeasurementIds;
    }

    /** Custom measurements split by context kind (for the dedicated panel sections). */
    getCustomMeasurementsByCategory(): {
        lengths: MeasurementResult[];
        angles: MeasurementResult[];
        diameters: MeasurementResult[];
    } {
        const result = {
            lengths: [] as MeasurementResult[],
            angles: [] as MeasurementResult[],
            diameters: [] as MeasurementResult[],
        };
        for (const id of this.customMeasurementIds) {
            const m = this.measurements.get(id);
            if (!m) continue;
            switch (m.context.type) {
                case 'custom-length':
                    result.lengths.push(m);
                    break;
                case 'custom-angle':
                    result.angles.push(m);
                    break;
                case 'custom-diameter':
                    result.diameters.push(m);
                    break;
            }
        }
        return result;
    }

    // ========================================================================
    // Custom-measurement draft lifecycle
    // ========================================================================

    /** Begin a new draft, cancelling any current draft and clearing selection. */
    beginCustomDraft(kind: DraftKind): void {
        if (this.selectedMeasurementId !== null) {
            this.deselectMeasurement();
        }
        this.draft = { kind, points: {} };
        this.emit(this.EVENTS.CUSTOM_MEASUREMENT_DRAFT_CHANGED, { draft: this.draft });
    }

    /**
     * Assign the current centerline location to a draft slot. Auto-commits once
     * every slot is filled and the result is computable (no explicit Confirm).
     */
    setDraftPointToCurrentLocation(slot: DraftSlot): void {
        if (!this.draft || !requiredSlots(this.draft.kind).includes(slot)) return;

        const locationService = this.resolveLocationService();
        if (!locationService) return;

        const location = locationService.getLocation();
        if (!location?.centerlinePosition) return;

        const { centerline, nodeIndex } = location.centerlinePosition;
        const node = centerline.getNodeAt(nodeIndex);

        this.draft = {
            kind: this.draft.kind,
            points: { ...this.draft.points, [slot]: node.id },
        };

        // Auto-commit when all slots are filled and the measurement is computable.
        // A complete-but-invalid draft (e.g. cross-leg length) is left intact so
        // its error surfaces in the UI and the user can re-assign a point.
        if (this.isDraftComplete() && this.isDraftComputable(this.draft)) {
            this.commitDraft();
            return;
        }

        this.emit(this.EVENTS.CUSTOM_MEASUREMENT_DRAFT_CHANGED, { draft: this.draft });
    }

    cancelDraft(): void {
        if (!this.draft) return;
        this.draft = null;
        this.emit(this.EVENTS.CUSTOM_MEASUREMENT_DRAFT_CHANGED, { draft: null });
    }

    isDraftComplete(): boolean {
        if (!this.draft) return false;
        return requiredSlots(this.draft.kind).every((slot) => this.draft!.points[slot] != null);
    }

    /**
     * Whether the in-progress draft is complete AND resolves to a computable
     * measurement (e.g. a length whose endpoints share a vessel path). Gates the
     * Confirm button so committing can never silently no-op.
     */
    canCommitDraft(): boolean {
        if (!this.draft || !this.isDraftComplete()) return false;
        return this.isDraftComputable(this.draft);
    }

    /** True when a complete draft resolves to a computable measurement. */
    private isDraftComputable(draft: CustomMeasurementDraft): boolean {
        const { kind, points } = draft;
        if (kind === 'length') {
            return computeLength(points.start!, points.end!, this.centerlines) != null;
        }
        const nodeIds: [number, number, number] = [points.p1!, points.vertex!, points.p2!];
        return computeAngleAtNodes(nodeIds, this.nodePool, CUSTOM_ANGLE_FORMULA) !== null;
    }

    getDraft(): CustomMeasurementDraft | null {
        return this.draft;
    }

    /**
     * Commit handler for the ✓ Confirm button. No-ops (returns null) unless the
     * draft is complete AND the measurement can be computed (e.g. not cross-leg).
     * On failure the draft is left intact so the user can re-pick.
     */
    commitDraft(): string | null {
        if (!this.draft || !this.isDraftComplete()) return null;

        // Validate computability BEFORE mutating state (e.g. reject cross-leg lengths).
        if (!this.isDraftComputable(this.draft)) return null;

        const { kind, points } = this.draft;

        // Clear the draft BEFORE selecting so the "selection cancels draft" guard
        // doesn't cancel the freshly committed measurement.
        this.draft = null;
        this.emit(this.EVENTS.CUSTOM_MEASUREMENT_DRAFT_CHANGED, { draft: null });

        let newId: string;
        if (kind === 'length') {
            const counter = this.nextCustomLengthId;
            this.addCustomLength(points.start!, points.end!);
            newId = `custom-length-${counter}`;
        } else {
            const counter = this.nextCustomAngleId;
            this.addCustomAngle([points.p1!, points.vertex!, points.p2!]);
            newId = `custom-angle-${counter}`;
        }

        this.selectMeasurement(newId);
        return newId;
    }

    /**
     * Re-assign one point of a committed custom length/angle to the current
     * location, recomputing its value in place.
     */
    reassignCustomMeasurementPoint(measurementId: string, slot: DraftSlot): boolean {
        if (!this.customMeasurementIds.has(measurementId)) return false;
        const existing = this.measurements.get(measurementId);
        if (!existing) return false;

        const locationService = this.resolveLocationService();
        if (!locationService) return false;
        const location = locationService.getLocation();
        if (!location?.centerlinePosition) return false;

        const { centerline, nodeIndex } = location.centerlinePosition;
        const node = centerline.getNodeAt(nodeIndex);
        const ctx = existing.context;

        if (ctx.type === 'custom-length') {
            if (slot !== 'start' && slot !== 'end') return false;
            const fromNodeId = slot === 'start' ? node.id : ctx.fromNodeId;
            const toNodeId = slot === 'end' ? node.id : ctx.toNodeId;
            const lengthResult = computeLength(fromNodeId, toNodeId, this.centerlines);
            if (!lengthResult) return false;

            this.measurements.set(
                measurementId,
                existing.with({
                    value: lengthResult.value,
                    navigationNodeId: fromNodeId,
                    hasEditedInputs: true,
                    context: { type: 'custom-length', fromNodeId, toNodeId },
                })
            );
        } else if (ctx.type === 'custom-angle') {
            const slotIndex = ANGLE_SLOTS.indexOf(slot as AngleSlot);
            if (slotIndex < 0) return false;

            const nodeIds = [...ctx.nodeIds] as [number, number, number];
            nodeIds[slotIndex] = node.id;
            const value = computeAngleAtNodes(nodeIds, this.nodePool, ctx.formula);
            if (value === null) return false;

            this.measurements.set(
                measurementId,
                existing.with({
                    value,
                    navigationNodeId: nodeIds[1],
                    hasEditedInputs: true,
                    context: { type: 'custom-angle', nodeIds, formula: ctx.formula },
                })
            );
        } else {
            return false;
        }

        this.emit(this.EVENTS.MEASUREMENT_RELOCATED, { measurementId, newNodeId: node.id });
        this.broadcastMeasurementsUpdated([measurementId]);
        return true;
    }

    // ========================================================================
    // Measurement Selection
    // ========================================================================

    selectMeasurement(id: string): void {
        // Selecting another measurement cancels any in-progress draft creation.
        if (this.draft) this.cancelDraft();
        if (this.selectedMeasurementId === id) return;
        this.selectedMeasurementId = id;
        this.emit(this.EVENTS.MEASUREMENT_SELECTION_CHANGED, { selectedId: id });
    }

    deselectMeasurement(): void {
        if (this.selectedMeasurementId === null) return;
        this.selectedMeasurementId = null;
        this.emit(this.EVENTS.MEASUREMENT_SELECTION_CHANGED, { selectedId: null });
    }

    getSelectedMeasurementId(): string | null {
        return this.selectedMeasurementId;
    }

    // ========================================================================
    // Measurement Access
    // ========================================================================

    getMeasurement(id: string): MeasurementResult | undefined {
        return this.measurements.get(id);
    }

    getAllMeasurements(): ReadonlyMap<string, MeasurementResult> {
        return this.measurements;
    }

    /**
     * Get measurements grouped by category for sidebar display.
     */
    getMeasurementsByCategory(): {
        lengths: MeasurementResult[];
        diameters: MeasurementResult[];
        angles: MeasurementResult[];
        derived: MeasurementResult[];
    } {
        const groups = {
            lengths: [] as MeasurementResult[],
            diameters: [] as MeasurementResult[],
            angles: [] as MeasurementResult[],
            derived: [] as MeasurementResult[],
        };

        for (const [, m] of this.measurements) {
            switch (m.category) {
                case MeasurementCategory.LENGTH:
                    groups.lengths.push(m);
                    break;
                case MeasurementCategory.DIAMETER:
                    groups.diameters.push(m);
                    break;
                case MeasurementCategory.ANGLE:
                    groups.angles.push(m);
                    break;
                case MeasurementCategory.DERIVED:
                    groups.derived.push(m);
                    break;
            }
        }

        return groups;
    }

    // ========================================================================
    // Visibility
    // ========================================================================

    setVisibility(measurementId: string, visible: boolean): void {
        this.visibility.set(measurementId, visible);
    }

    isVisible(measurementId: string): boolean {
        return this.visibility.get(measurementId) ?? true;
    }

    // ========================================================================
    // Persistence
    // ========================================================================

    /**
     * Serialize the current sizing state for API persistence.
     * Only includes user modifications, not computed values.
     */
    serializeState(): ISizingStateDTO {
        const editedKeypoints: Array<{ keypointId: string; nodeId: number }> = [];
        for (const [id, placement] of this.placements) {
            if (placement.isEdited) {
                editedKeypoints.push({ keypointId: id, nodeId: placement.nodeId });
            }
        }

        const diameterEdits: Array<{ nodeId: number; editedValue: number; p1: Vec3; p2: Vec3 }> = [];
        for (const [, edit] of this.diameterEdits) {
            if (edit.isModified) {
                diameterEdits.push({
                    nodeId: edit.nodeId,
                    editedValue: edit.editedValue,
                    p1: edit.p1,
                    p2: edit.p2,
                });
            }
        }

        const customOffsets = this.customOffsets.map((o) => ({
            keypointId: o.keypointId,
            offsetMm: o.offsetMm,
        }));

        const customMeasurements: ICustomMeasurementDTO[] = [];
        for (const id of this.customMeasurementIds) {
            const m = this.measurements.get(id);
            if (!m) continue;
            if (m.context.type === 'custom-length') {
                customMeasurements.push({
                    type: 'custom-length',
                    name: m.name,
                    fromNodeId: m.context.fromNodeId,
                    toNodeId: m.context.toNodeId,
                });
            } else if (m.context.type === 'custom-diameter') {
                customMeasurements.push({
                    type: 'custom-diameter',
                    name: m.name,
                    nodeId: m.context.nodeId,
                });
            } else if (m.context.type === 'custom-angle') {
                customMeasurements.push({
                    type: 'custom-angle',
                    name: m.name,
                    nodeIds: m.context.nodeIds,
                });
            }
        }

        const visibility: Record<string, boolean> = {};
        for (const [id, visible] of this.visibility) {
            visibility[id] = visible;
        }

        return {
            version: 1,
            templateId: this.template?.id ?? '',
            editedKeypoints,
            diameterEdits,
            customOffsets,
            customMeasurements,
            visibility,
        };
    }

    // ========================================================================
    // Accessors for display layer
    // ========================================================================

    getTemplate(): ISizingTemplate | null {
        return this.template;
    }

    getCenterlines(): ReadonlyMap<string, Centerline> {
        return this.centerlines;
    }

    getNodePool(): ReadonlyMap<number, CenterlineNode> {
        return this.nodePool;
    }

    getGhostPlacements(): ReadonlyMap<string, readonly KeypointPlacement[]> {
        return this.ghostPlacements;
    }

    // ========================================================================
    // Domain Accessors
    // ========================================================================

    /**
     * Get template keypoints that are editable.
     */
    getEditableKeypoints(): IKeypointDefinition[] {
        return this.template?.keypoints.filter((kp) => kp.editable) ?? [];
    }

    /**
     * Group diameter measurements by type: main diameters grouped by keypoint,
     * min/max diameters, and custom diameters.
     */
    getDiameterGroups(): DiameterGroupsResult {
        const diameters = this.getMeasurementsByCategory().diameters;

        const groups: DiameterGroup[] = [];
        const minMax: MeasurementResult[] = [];
        const custom: MeasurementResult[] = [];

        const mainByKeypoint = new Map<string, MeasurementResult>();
        const offsetsByKeypoint = new Map<string, MeasurementResult[]>();

        for (const m of diameters) {
            switch (m.context.type) {
                case 'diameter':
                    mainByKeypoint.set(m.id, m);
                    break;
                case 'offset-diameter':
                    if (!offsetsByKeypoint.has(m.context.keypointId)) {
                        offsetsByKeypoint.set(m.context.keypointId, []);
                    }
                    offsetsByKeypoint.get(m.context.keypointId)!.push(m);
                    break;
                case 'min-max-diameter':
                    minMax.push(m);
                    break;
                case 'custom-diameter':
                    custom.push(m);
                    break;
            }
        }

        for (const [, m] of mainByKeypoint) {
            if (m.context.type !== 'diameter') continue;
            const keypointId = m.context.keypointId;
            groups.push({
                keypointId,
                main: m,
                offsets: offsetsByKeypoint.get(keypointId) ?? [],
            });
        }

        return { groups, minMax, custom };
    }

    // ========================================================================
    // Facade Methods (bridge to CurrentLocationService)
    // ========================================================================

    /**
     * Move a keypoint to the current location on the centerline.
     * @returns false if no valid location is available.
     */
    moveKeypointToCurrentLocation(keypointId: string): boolean {
        const locationService = this.resolveLocationService();
        if (!locationService) return false;

        const location = locationService.getLocation();
        if (!location?.centerlinePosition) return false;

        const { centerline, nodeIndex } = location.centerlinePosition;
        const node = centerline.getNodeAt(nodeIndex);

        this.moveKeypoint(keypointId, node.id);
        return true;
    }

    /**
     * Add a custom diameter measurement at the current location.
     * @returns the new measurement ID, or null if no valid location.
     */
    addCustomDiameterAtCurrentLocation(): string | null {
        const locationService = this.resolveLocationService();
        if (!locationService) return null;

        const location = locationService.getLocation();
        if (!location?.centerlinePosition) return null;

        const { centerline, nodeIndex } = location.centerlinePosition;
        const node = centerline.getNodeAt(nodeIndex);

        this.addCustomDiameter(node.id);
        // Return the ID of the most recently added custom diameter
        const lastId = `custom-diameter-${this.nextCustomDiameterId - 1}`;
        return this.measurements.has(lastId) ? lastId : null;
    }

    /**
     * Relocate a measurement to the current location on the centerline.
     * @returns false if no valid location is available.
     */
    relocateMeasurementToCurrentLocation(measurementId: string): boolean {
        const locationService = this.resolveLocationService();
        if (!locationService) return false;

        const location = locationService.getLocation();
        if (!location?.centerlinePosition) return false;

        const { centerline, nodeIndex } = location.centerlinePosition;
        const node = centerline.getNodeAt(nodeIndex);

        this.relocateMeasurement(measurementId, node.id);
        return true;
    }

    /**
     * Navigate to a centerline node by setting the current world position.
     */
    navigateToNode(nodeId: number): void {
        const node = this.nodePool.get(nodeId);
        if (!node) return;

        const locationService = this.resolveLocationService();
        if (!locationService) return;

        locationService.setCurrentWorldPosition(node.point);
    }

    /**
     * Navigate to a measurement's representative node.
     */
    navigateToMeasurement(measurementId: string): void {
        const measurement = this.measurements.get(measurementId);
        if (!measurement) return;

        const nodeId = getNavigationNodeId(measurement, this);
        if (nodeId != null) {
            this.navigateToNode(nodeId);
        }
    }

    // ========================================================================
    // Reset / cleanup
    // ========================================================================

    override reset(): void {
        this.template = null;
        this.centerlines.clear();
        this.nodePool.clear();
        this.initialPlacements.clear();
        this.placements.clear();
        this.ghostPlacements.clear();
        this.diameterEdits.clear();
        this.customOffsets = [];
        this.customMeasurementIds.clear();
        this.measurements.clear();
        this.visibility.clear();
        this.selectedMeasurementId = null;
        this.depGraph = null;
        this.nextOffsetId = 1;
        this.nextCustomLengthId = 1;
        this.nextCustomDiameterId = 1;
        this.nextCustomAngleId = 1;
        this.draft = null;
        super.reset();
    }

    // ========================================================================
    // Private helpers
    // ========================================================================

    /**
     * Type-safe wrapper around broadcast that enforces the correct payload
     * type for each event name via the SizingEventMap.
     */
    private emit<E extends keyof SizingEventMap>(
        event: E,
        ...[data]: SizingEventMap[E] extends undefined ? [undefined?] : [SizingEventMap[E]]
    ): void {
        this.broadcast(event, data);
    }

    /**
     * Lazily resolve CurrentLocationService from the service manager.
     */
    private resolveLocationService(): CurrentLocationService | null {
        try {
            return this.serviceManager.getService<CurrentLocationService>(
                CurrentLocationService.SERVICE_NAME
            );
        } catch {
            return null;
        }
    }

    private buildNodePool(): void {
        this.nodePool.clear();
        for (const [, centerline] of this.centerlines) {
            for (const node of centerline.nodes) {
                this.nodePool.set(node.id, node);
            }
        }
    }

    /**
     * Project a keypoint's 3D position onto the closest centerline node.
     */
    private projectKeypointOntoNode(_kpDef: IKeypointDefinition, position: Vec3): KeypointPlacement | null {
        let bestNodeId = -1;
        let bestDistSq = Infinity;
        let bestPosition: Vec3 | null = null;

        for (const [, centerline] of this.centerlines) {
            const result = centerline.findClosestPoint(position);
            if (result.error * result.error < bestDistSq) {
                bestDistSq = result.error * result.error;
                bestNodeId = centerline.nodes[result.nodeIndex].id;
                bestPosition = centerline.nodes[result.nodeIndex].point;
            }
        }

        if (bestNodeId < 0 || !bestPosition) return null;

        return new KeypointPlacement(_kpDef.id, bestNodeId, bestPosition, false);
    }

    private buildComputationContext(): ComputationContext {
        if (!this.template) {
            throw new Error('SizingStateService not initialized: template is null');
        }
        return {
            template: this.template,
            placements: this.placements,
            centerlines: this.centerlines,
            nodePool: this.nodePool,
            diameterEdits: this.diameterEdits,
            customOffsets: this.customOffsets,
            ghostPlacements: this.ghostPlacements,
        };
    }

    private recomputeForKeypoint(keypointId: string): string[] {
        if (!this.depGraph) return [];

        const affected = getAffectedMeasurements([keypointId], this.depGraph);
        MeasurementEngine.recompute(affected, this.buildComputationContext(), this.measurements);
        return [...affected];
    }

    private recomputeForDiameterEdit(editedNodeId?: number): string[] {
        if (!this.template || !this.depGraph) return [];

        const affected = getAffectedByDiameterEdit(this.template, this.depGraph, this.customOffsets);
        MeasurementEngine.recompute(affected, this.buildComputationContext(), this.measurements);

        const customUpdated = this.recomputeCustomDiametersAtNode(editedNodeId);
        return [...affected, ...customUpdated];
    }

    /**
     * Recompute custom diameter measurements whose nodeId matches the edited node.
     * Custom measurements live outside the template dependency graph so they
     * need to be refreshed manually when the underlying diameter changes.
     */
    private recomputeCustomDiametersAtNode(nodeId?: number): string[] {
        if (nodeId == null) return [];

        const updated: string[] = [];
        for (const id of this.customMeasurementIds) {
            const m = this.measurements.get(id);
            if (!m || m.context.type !== 'custom-diameter' || m.context.nodeId !== nodeId) continue;

            const node = this.nodePool.get(nodeId);
            if (!node) continue;

            const edit = this.diameterEdits.get(nodeId);
            const value = edit?.editedValue ?? node.diameter.value;

            this.measurements.set(
                id,
                m.with({
                    value,
                    hasEditedInputs: true,
                    context: { type: 'custom-diameter', nodeId },
                })
            );
            updated.push(id);
        }
        return updated;
    }

    private addOffsetDiameterInternal(keypointId: string, offsetMm: number, broadcastEvents: boolean): void {
        const offsetId = `custom-offset-${this.nextOffsetId++}`;
        const offsetLabel = `D. ${keypointId} ${offsetMm >= 0 ? '+' : '-'}${Math.abs(offsetMm)}`;
        const def: IOffsetDiameterMeasurementDef = {
            type: MeasurementType.OFFSET_DIAMETER,
            id: offsetId,
            name: offsetLabel,
            shortLabel: offsetLabel,
            category: MeasurementCategory.DIAMETER,
            keypointId,
            offsetMm,
            isDefault: false,
        };

        this.customOffsets.push(def);

        // Compute this single measurement
        const result = MeasurementEngine.computeOne(def, this.buildComputationContext(), this.measurements);
        if (result) {
            this.measurements.set(result.id, result);
        }

        if (broadcastEvents) {
            this.emit(this.EVENTS.OFFSET_ADDED, { offsetId, keypointId, offsetMm });
            this.broadcastMeasurementsUpdated([offsetId]);
        }
    }

    /**
     * Compute ghost placements for a keypoint that has ghostOnOtherLegs enabled.
     * For each centerline that does NOT contain the primary placement's node,
     * finds the node at the same cumulative distance from root.
     *
     * NOTE: This assumes centerlines are immutable after initialization.
     * If centerlines are ever updated post-init, ghost placements must be recomputed.
     */
    private computeGhostPlacements(
        kpDef: IKeypointDefinition,
        primaryPlacement: KeypointPlacement
    ): KeypointPlacement[] {
        if (!kpDef.ghostOnOtherLegs) return [];

        const ghosts: KeypointPlacement[] = [];
        const primaryNodeId = primaryPlacement.nodeId;

        // Find the primary centerline and distance from root
        let primaryDistance: number | null = null;
        let primaryIdx = -1;
        for (const [, centerline] of this.centerlines) {
            const idx = centerline.getIndexByNodeId(primaryNodeId);
            if (idx >= 0) {
                primaryDistance = centerline.getDistanceAt(idx);
                primaryIdx = idx;
                break;
            }
        }
        if (primaryDistance === null) return [];

        // Guard against data integrity issue: non-root node reporting zero distance
        if (primaryDistance === 0 && primaryIdx > 0) {
            console.warn(
                `[SizingStateService] Ghost placement skipped: node ${primaryNodeId} at index ${primaryIdx} ` +
                    `reports zero distance from root, which indicates a data integrity issue.`
            );
            return [];
        }

        // For each other centerline, find the node at the same distance from root
        for (const [, centerline] of this.centerlines) {
            if (centerline.hasNode(primaryNodeId)) continue;
            if (centerline.nodeCount === 0) continue;

            const ghostIdx = centerline.getIndexAtDistance(primaryDistance);
            if (ghostIdx < 0 || ghostIdx >= centerline.nodeCount) continue;

            const ghostNode = centerline.getNodeAt(ghostIdx);

            ghosts.push(
                new KeypointPlacement(kpDef.id, ghostNode.id, ghostNode.point, primaryPlacement.isEdited)
            );
        }

        return ghosts;
    }

    /**
     * Recompute ghost placements for a single keypoint if it has ghostOnOtherLegs enabled.
     */
    private updateGhostPlacementsForKeypoint(keypointId: string): void {
        const kpDef = this.template?.keypoints.find((k) => k.id === keypointId);
        if (!kpDef?.ghostOnOtherLegs) return;

        const placement = this.placements.get(keypointId);
        if (!placement) return;

        const ghosts = this.computeGhostPlacements(kpDef, placement);
        if (ghosts.length > 0) {
            this.ghostPlacements.set(keypointId, ghosts);
        } else {
            this.ghostPlacements.delete(keypointId);
        }
    }

    /**
     * Recompute ghost placements for all keypoints that have ghostOnOtherLegs enabled.
     */
    private updateAllGhostPlacements(): void {
        this.ghostPlacements.clear();
        if (!this.template) return;

        for (const kpDef of this.template.keypoints) {
            if (!kpDef.ghostOnOtherLegs) continue;
            this.updateGhostPlacementsForKeypoint(kpDef.id);
        }
    }

    private broadcastMeasurementsUpdated(updatedIds: readonly string[]): void {
        if (updatedIds.length > 0) {
            this.emit(this.EVENTS.MEASUREMENTS_UPDATED, {
                updatedIds,
            });
        }
    }
}
